"use client"

import { forwardRef, useImperativeHandle, useRef } from "react"
import { Business, Contact, Household, Team, User } from "@/schema"
import { TaskDraft } from "@/stores/use-task-store"
import { cn } from "@ui/utils"
import { LexicalEditor } from "lexical"

import type { EditorRefHandle } from "@/components/lexical"
import { MentionUpdateData } from "@/components/tasks/hooks/use-task-mentions"
import TaskEditor from "@/components/tasks/task-editor"

import TaskAssignmentSelector from "./inputs/task-assignment-selector"
import TaskCreateDateSelector from "./inputs/task-create-date-selector"
import TaskCreateLabelSelector from "./inputs/task-create-label-selector"
import TaskCreatePrioritySelector from "./inputs/task-create-priority-selector"
import TaskCreateRecurrenceSelector from "./inputs/task-create-recurrence-selector"
import TaskCreateRelationshipSelector from "./inputs/task-create-relationship-selector"

export interface TaskCreateFormHandle {
  focusEditor: () => void
  getEditorContainer: () => HTMLElement | null
  getDescriptionEditor: () => LexicalEditor | null
}

export interface TaskCreateFormProps {
  draft: TaskDraft
  editorKey?: string
  onDraftChange: (updates: Partial<TaskDraft>) => void
  selectedHousehold: Household | null
  onHouseholdChange: (household: Household | null) => void
  selectedContact: Contact | null
  onContactChange: (contact: Contact | null) => void
  selectedBusiness: Business | null
  onBusinessChange: (business: Business | null) => void
  selectedAssignee: User | null
  onAssigneeChange: (assignee: User | null) => void
  selectedTeam: Team | null
  onTeamChange: (team: Team | null) => void
  selectedAssignedToCustomFieldId?: string | null
  onAssignedToCustomFieldChange?: (customFieldId: string | null) => void
  onMentionsUpdate?: (updates: MentionUpdateData) => void
  className?: string
  showRecurrence?: boolean
  onDateChange?: (date: Date | undefined, allDay: boolean) => void
  showAssigneeSelector?: boolean
  showTeamSelector?: boolean
  showDateSelector?: boolean
  includeUserMentions?: boolean
  allowHouseholds?: boolean
  allowContacts?: boolean
  allowBusinesses?: boolean
  relationshipSelectorDisabled?: boolean
  relationshipBookId?: string
  assignmentBookId?: string
}

export const TaskCreateForm = forwardRef<TaskCreateFormHandle, TaskCreateFormProps>(
  (
    {
      draft,
      editorKey,
      onDraftChange,
      selectedHousehold,
      onHouseholdChange,
      selectedContact,
      onContactChange,
      selectedBusiness,
      onBusinessChange,
      selectedAssignee,
      onAssigneeChange,
      selectedTeam,
      onTeamChange,
      selectedAssignedToCustomFieldId,
      onAssignedToCustomFieldChange,
      onMentionsUpdate,
      className,
      showRecurrence = true,
      onDateChange,
      showAssigneeSelector = true,
      showTeamSelector = true,
      showDateSelector = true,
      includeUserMentions = true,
      allowHouseholds = true,
      allowContacts = true,
      allowBusinesses = true,
      relationshipSelectorDisabled = false,
      relationshipBookId,
      assignmentBookId,
    },
    ref
  ) => {
    const editorRef = useRef<EditorRefHandle | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)

    useImperativeHandle(ref, () => ({
      focusEditor: () => editorRef.current?.focus(),
      getEditorContainer: () => containerRef.current,
      getDescriptionEditor: () => editorRef.current?.getDescriptionEditor?.() ?? null,
    }))

    const handleDateChange = (date: Date | undefined) => {
      const nextUpdates: Partial<TaskDraft> = { date }
      let allDay = true

      if (date) {
        const isBeginningOfDay = date.getHours() === 0 && date.getMinutes() === 0
        allDay = isBeginningOfDay
      }

      nextUpdates.allDay = allDay
      onDraftChange(nextUpdates)
      onDateChange?.(date, allDay)
    }

    const recurrenceEnabled = showRecurrence && draft.recurrenceEnabled

    return (
      <div ref={containerRef} className={cn("flex flex-col", className)}>
        <TaskEditor
          ref={editorRef}
          key={editorKey}
          onChange={(lexicalText, fieldType) => {
            if (fieldType === "title") {
              onDraftChange({ title: lexicalText })
            } else {
              onDraftChange({ description: lexicalText })
            }
          }}
          initialTitleContent={draft.title}
          initialDescriptionContent={draft.description}
          onMentionsUpdate={onMentionsUpdate}
          includeUserMentions={includeUserMentions}
          hasExistingRelationship={!!(selectedHousehold || selectedContact || selectedBusiness)}
        />

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <TaskCreatePrioritySelector
            priority={draft.priority}
            onPriorityChange={(priority) => onDraftChange({ priority })}
          />
          <TaskCreateLabelSelector
            label={draft.label}
            onLabelChange={(nextLabel) => onDraftChange({ label: nextLabel })}
          />
          {(showAssigneeSelector || showTeamSelector) && (
            <TaskAssignmentSelector
              selectedAssignee={selectedAssignee}
              setSelectedAssignee={onAssigneeChange}
              selectedTeam={selectedTeam}
              setSelectedTeam={onTeamChange}
              selectedUserReferenceFieldId={selectedAssignedToCustomFieldId}
              setSelectedUserReferenceFieldId={onAssignedToCustomFieldChange}
              includeUserReferenceFields={allowHouseholds}
              bookId={assignmentBookId}
            />
          )}

          {(allowHouseholds || allowContacts || allowBusinesses) && (
            <TaskCreateRelationshipSelector
              selectedHousehold={selectedHousehold}
              onHouseholdSelect={(household) => {
                onHouseholdChange(household)
                if (household) {
                  onContactChange(null)
                  onBusinessChange(null)
                }
              }}
              selectedContact={selectedContact}
              onContactSelect={(contact) => {
                onContactChange(contact)
                if (contact) {
                  onHouseholdChange(null)
                  onBusinessChange(null)
                }
              }}
              selectedBusiness={selectedBusiness}
              onBusinessSelect={(business) => {
                onBusinessChange(business)
                if (business) {
                  onHouseholdChange(null)
                  onContactChange(null)
                }
              }}
              allowHouseholds={allowHouseholds}
              allowContacts={allowContacts}
              allowBusinesses={allowBusinesses}
              disabled={relationshipSelectorDisabled}
              bookId={relationshipBookId}
            />
          )}

          {showDateSelector && (
            <div className={cn(recurrenceEnabled && "pointer-events-none opacity-60")}>
              <TaskCreateDateSelector date={draft.date} setDate={handleDateChange} />
            </div>
          )}
        </div>
        {showRecurrence && (
          <TaskCreateRecurrenceSelector
            enabled={draft.recurrenceEnabled}
            intervalValue={draft.recurrenceIntervalValue}
            intervalUnit={draft.recurrenceIntervalUnit}
            monthlyRuleType={draft.recurrenceMonthlyRuleType}
            weekday={draft.recurrenceWeekday}
            weekdayOrdinal={draft.recurrenceWeekdayOrdinal}
            startDate={draft.recurrenceStart}
            endDate={draft.recurrenceEnd}
            onChange={(updates) => {
              const nextUpdates: Partial<TaskDraft> = {}

              if (updates.intervalValue !== undefined) {
                nextUpdates.recurrenceIntervalValue = updates.intervalValue ?? null
              }

              if (updates.intervalUnit !== undefined) {
                nextUpdates.recurrenceIntervalUnit = updates.intervalUnit
              }

              if (updates.monthlyRuleType !== undefined) {
                nextUpdates.recurrenceMonthlyRuleType = updates.monthlyRuleType ?? "calendar_day"
              }

              if (updates.weekday !== undefined) {
                nextUpdates.recurrenceWeekday = updates.weekday
              }

              if (updates.weekdayOrdinal !== undefined) {
                nextUpdates.recurrenceWeekdayOrdinal = updates.weekdayOrdinal
              }

              if (updates.startDate !== undefined) {
                nextUpdates.recurrenceStart = updates.startDate ?? undefined
                nextUpdates.allDay = true
              }

              if (updates.endDate !== undefined) {
                nextUpdates.recurrenceEnd = updates.endDate ?? undefined
              }

              if (updates.enabled !== undefined) {
                nextUpdates.recurrenceEnabled = updates.enabled
              }

              onDraftChange(nextUpdates)
            }}
            onClear={() => {
              onDraftChange({
                recurrenceEnabled: false,
                recurrenceStart: undefined,
                recurrenceEnd: undefined,
                recurrenceIntervalValue: null,
                recurrenceIntervalUnit: null,
                recurrenceMonthlyRuleType: "calendar_day",
                recurrenceWeekday: null,
                recurrenceWeekdayOrdinal: null,
              })
            }}
            className="mt-2"
          />
        )}
      </div>
    )
  }
)

TaskCreateForm.displayName = "TaskCreateForm"
